import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from '../../utils/toast';
import {
  GalleryEntityType,
  GalleryImage,
  MAX_FILES_PER_UPLOAD,
  deleteImage,
  listImages,
  readImageError,
  reorderImages,
  setPrimaryImage,
  uploadImages,
} from '../../api/images-client';
import { ConfirmDialog } from '../modal/confirm-dialog';
import { ImageCropModal } from './image-crop-modal';
import { ImageViewer } from './image-viewer';
import { Presence, T } from '../../motion';
import {
  AddGlyph,
  AddTile,
  Count,
  CoverBadge,
  Empty,
  EmptyText,
  EmptyTitle,
  Grid,
  Notice,
  ProgressFill,
  ProgressTrack,
  Section,
  SectionHead,
  SectionTitle,
  Tile,
  TileActions,
  TileButton,
  TileImage,
  TILE_ACTIONS_CLASS,
} from './entity-gallery.styled';

/**
 * The photo gallery for one building or one unit.
 *
 * ONE COMPONENT FOR BOTH, because every operation is identical and the only
 * difference is two words in a URL. Two components would be two of every bug.
 *
 * WHY THE URLS ARE REFETCHED ON A TIMER
 *
 * The bucket blocks public access, so every `url` here is a presigned link with
 * an expiry (the server sends `expires_in` alongside). A dashboard left open on
 * a front-desk monitor overnight would otherwise decay into a grid of broken
 * images with no error anywhere — the <img> tags just start 403ing. The refresh
 * below re-lists a little before the signatures lapse.
 */

interface EntityGalleryProps {
  type: GalleryEntityType;
  entityId: number;
  // Does this user hold building:edit / unit:edit? Read-only viewers still get
  // the grid and the lightbox; they just get no add, reorder, cover or delete.
  canEdit: boolean;
  title?: string;
}

// Refresh at 80% of the signature's life. Not at 100%: a slow render or a
// backgrounded tab would land the refetch after the URLs are already dead.
const REFRESH_SAFETY = 0.8;

// A ceiling on the ORIGINAL file, enforced only to keep the browser's canvas
// decode from exhausting memory. Generous: a 40 MP phone photo is ~15 MB.
const MAX_SOURCE_MB = 40;
const MAX_SOURCE_BYTES = MAX_SOURCE_MB * 1024 * 1024;

/* THE IMAGE FADES IN WHEN IT HAS LOADED, not when its tile mounts. The tile's
   tinted ground is the placeholder; the picture arrives over it on its own
   `load` event. A cached image can fire `load` before React attaches the
   handler, so the mount effect also reads `complete` - otherwise a cached
   photo stayed invisible. Re-armed when `src` changes (the URL refresh path). */
const FadingTileImage: React.FC<{ src: string; onClick: () => void }> = ({
  src,
  onClick,
}) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = imgRef.current;
    setLoaded(!!el && el.complete && el.naturalWidth > 0);
  }, [src]);

  return (
    <TileImage
      ref={imgRef}
      src={src}
      alt=""
      loading="lazy"
      initial={{ opacity: 0 }}
      animate={{ opacity: loaded ? 1 : 0 }}
      transition={T.enterFast}
      onLoad={() => setLoaded(true)}
      onClick={onClick}
    />
  );
};

export const EntityGallery: React.FC<EntityGalleryProps> = ({
  type,
  entityId,
  canEdit,
  title = 'Photos',
}) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [viewerAt, setViewerAt] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<GalleryImage | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const rows = await listImages(type, entityId);
      setImages(rows);
      setError('');
    } catch (e) {
      setError(readImageError(e, 'Could not load the photos'));
    } finally {
      setLoading(false);
    }
  }, [type, entityId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Keep the presigned URLs alive for as long as the screen is open.
  useEffect(() => {
    const ttl = images[0]?.expires_in;
    if (!ttl) return undefined;

    const timer = window.setTimeout(load, ttl * 1000 * REFRESH_SAFETY);

    return () => window.clearTimeout(timer);
  }, [images, load]);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    // Reset immediately so picking the SAME file twice in a row still fires a
    // change event the second time.
    event.target.value = '';
    if (chosen.length === 0) return;

    if (chosen.length > MAX_FILES_PER_UPLOAD) {
      toast.error(
        `Up to ${MAX_FILES_PER_UPLOAD} photos at a time — the first ${MAX_FILES_PER_UPLOAD} were taken.`
      );
    }

    // Reject enormous SOURCE files before anything tries to decode them.
    //
    // This is not the server's 8 MB limit — the cropped WebP that actually gets
    // uploaded is ~150 KB regardless of the original. The failure this prevents
    // is local: decoding a 60 MP image into a canvas allocates hundreds of MB,
    // and a phone browser answers by killing the tab. A tab that dies has no way
    // to show an error, so the check has to happen before the decode.
    const tooBig = chosen.filter((f) => f.size > MAX_SOURCE_BYTES);
    if (tooBig.length > 0) {
      toast.error(
        tooBig.length === 1
          ? `"${tooBig[0].name}" is very large (${Math.round(tooBig[0].size / 1024 / 1024)} MB). Please pick an image under ${MAX_SOURCE_MB} MB.`
          : `${tooBig.length} of those files are over ${MAX_SOURCE_MB} MB and were skipped.`
      );
    }

    const usable = chosen
      .filter((f) => f.size <= MAX_SOURCE_BYTES)
      .slice(0, MAX_FILES_PER_UPLOAD);
    if (usable.length === 0) return;

    setPending(usable);
  };

  const handleCropped = async (blobs: Blob[]) => {
    setPending([]);
    if (blobs.length === 0) return;

    setProgress(0);
    try {
      const created = await uploadImages(type, entityId, blobs, setProgress);
      // Append rather than refetch: the server returns the new rows already
      // presigned, so a second round trip would only cost latency.
      setImages((current) => [...current, ...created]);
      toast.success(
        created.length === 1 ? 'Photo added' : `${created.length} photos added`
      );
    } catch (e) {
      toast.error(readImageError(e, 'Could not upload the photos'));
    } finally {
      setProgress(null);
    }
  };

  const handleSetCover = async (image: GalleryImage) => {
    if (image.is_primary) return;

    // Optimistic: the cover moves under the cursor immediately. The database
    // enforces one-cover-per-entity with a partial unique index, so the server
    // cannot end up disagreeing with what is shown — it either accepts this or
    // errors, and the catch puts the old state back.
    const previous = images;
    setImages((current) =>
      current.map((row) => ({ ...row, is_primary: row.id === image.id }))
    );

    try {
      await setPrimaryImage(type, entityId, image.id);
    } catch (e) {
      setImages(previous);
      toast.error(readImageError(e, 'Could not set the cover photo'));
    }
  };

  const handleDelete = async () => {
    const target = confirmDelete;
    if (!target) return;

    const previous = images;
    setImages((current) => current.filter((row) => row.id !== target.id));
    setConfirmDelete(null);

    try {
      await deleteImage(type, entityId, target.id);
      // The server promotes the next photo to cover when the deleted one held
      // it. Re-listing is the honest way to reflect that — guessing which row
      // inherited it here would be a second implementation of that rule.
      if (target.is_primary) load();
    } catch (e) {
      setImages(previous);
      toast.error(readImageError(e, 'Could not delete the photo'));
    }
  };

  const commitOrder = async (ordered: GalleryImage[]) => {
    const previous = images;
    setImages(ordered);

    try {
      await reorderImages(
        type,
        entityId,
        ordered.map((row) => row.id)
      );
    } catch (e) {
      setImages(previous);
      toast.error(readImageError(e, 'Could not save the new order'));
    }
  };

  // HTML5 drag and drop rather than a library. The list is a flat grid of at
  // most a few dozen tiles, the drag never leaves the container, and the whole
  // interaction is three handlers — a sortable library would be more bytes than
  // the feature.
  const handleDrop = (targetId: string) => {
    setOverId(null);
    const sourceId = dragId;
    setDragId(null);
    if (!sourceId || sourceId === targetId) return;

    const from = images.findIndex((row) => row.id === sourceId);
    const to = images.findIndex((row) => row.id === targetId);
    if (from < 0 || to < 0) return;

    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commitOrder(next);
  };

  const busy = progress !== null;

  return (
    <Section>
      <SectionHead>
        <SectionTitle>
          {title}
          {images.length > 0 ? <Count>{images.length}</Count> : null}
        </SectionTitle>
      </SectionHead>

      {busy ? (
        <ProgressTrack aria-label="Upload progress">
          <ProgressFill $percent={progress ?? 0} />
        </ProgressTrack>
      ) : null}

      {error ? <Notice $tone="error">{error}</Notice> : null}

      {loading ? (
        <Notice $tone="muted">Loading photos…</Notice>
      ) : images.length === 0 && !canEdit ? (
        <Empty>
          <EmptyTitle>No photos yet</EmptyTitle>
          <EmptyText>
            Nobody has added photos here. Someone with edit access can.
          </EmptyText>
        </Empty>
      ) : (
        <Grid>
          {images.map((image, position) => (
            <Tile
              key={image.id}
              $dragging={dragId === image.id}
              $over={overId === image.id && dragId !== image.id}
              draggable={canEdit}
              onDragStart={() => canEdit && setDragId(image.id)}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onDragOver={(event) => {
                if (!canEdit || !dragId) return;
                // Without this the drop event never fires — the default action
                // for dragover is "reject the drop".
                event.preventDefault();
                setOverId(image.id);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (canEdit) handleDrop(image.id);
              }}
            >
              <FadingTileImage
                src={image.url}
                onClick={() => setViewerAt(position)}
              />
              {image.is_primary ? <CoverBadge>Cover</CoverBadge> : null}

              {canEdit ? (
                <TileActions className={TILE_ACTIONS_CLASS}>
                  <TileButton
                    type="button"
                    onClick={() => handleSetCover(image)}
                    disabled={image.is_primary}
                    title={
                      image.is_primary
                        ? 'This is the cover photo'
                        : 'Use as cover photo'
                    }
                  >
                    {image.is_primary ? 'Cover' : 'Make cover'}
                  </TileButton>
                  <TileButton
                    type="button"
                    $danger
                    onClick={() => setConfirmDelete(image)}
                    aria-label="Delete this photo"
                  >
                    Delete
                  </TileButton>
                </TileActions>
              ) : null}
            </Tile>
          ))}

          {canEdit ? (
            <AddTile
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              <AddGlyph aria-hidden="true">+</AddGlyph>
              {busy ? `Uploading ${progress}%` : 'Add photos'}
            </AddTile>
          ) : null}
        </Grid>
      )}

      {canEdit && images.length > 1 ? (
        <Notice $tone="muted">
          Drag a photo to reorder. The cover is what shows in lists and cards.
        </Notice>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={handleFiles}
      />

      <ImageCropModal
        files={pending}
        open={pending.length > 0}
        onCancel={() => setPending([])}
        onComplete={handleCropped}
      />

      {/* Inside a Presence so the lightbox animates out; its motion roots
          (scrim, stage, chrome) carry the exit. */}
      <Presence>
        {viewerAt !== null ? (
          <ImageViewer
            key="image-viewer"
            images={images}
            startIndex={viewerAt}
            onClose={() => setViewerAt(null)}
          />
        ) : null}
      </Presence>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete this photo?"
        message="It will be removed from the gallery and from storage. This cannot be undone."
        primaryLabel="Delete photo"
        primaryVariant="destructive"
        onPrimary={handleDelete}
        onDismiss={() => setConfirmDelete(null)}
      />
    </Section>
  );
};
